/**
 * Adapter puro entre o wire das rotas `produtos/previvaz-personalizado/estudos/*` e o
 * domínio da aba "Meus Estudos" (#1993). Sem React, store, fetch ou relógio.
 *
 * É o único módulo (junto com o slice de API) que conhece os tipos `Raw*` e os nomes de
 * parâmetro de wire. A validação de datas é estrita e feita por aritmética nas partes,
 * nunca com `new Date(string)`, para o resultado não depender do fuso da máquina.
 */

import { FLUX_STUDY_STATUS_CODES, FluxStudyStatusCode, isFluxStudyStatusCode } from 'constants/fluxStudyStatus';
import {
  DateOnly,
  Instant,
  MyStudiesPage,
  MyStudiesQuery,
  MyStudy,
  MyStudyBlock,
  MyStudyScenario,
  MyStudyStatusSnapshot,
  RawMyStudiesPage,
  RawMyStudy,
  RawMyStudyBlock,
  RawMyStudyScenario,
  RawMyStudyStatus,
  RawStudyCompanies,
  RawStudyCompany,
  RawStudyCompanyRef,
  RawStudyCreator,
  RawStudyCreatorRef,
  StudyCompaniesSummary,
  StudyCompanyOption,
  StudyCompanyRef,
  StudyCreatorOption,
  StudyCreatorRef,
  StudyOrigin,
} from 'types/Flux/Personalized/myStudies';

/* ------------------------------------------------------------------------------------ */
/* Erro de contrato                                                                     */
/* ------------------------------------------------------------------------------------ */

/** O backend mandou um campo obrigatório ausente ou malformado (falha alta, sem "Invalid Date"). */
export class MyStudiesContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MyStudiesContractError';
    Object.setPrototypeOf(this, MyStudiesContractError.prototype);
  }
}

/* ------------------------------------------------------------------------------------ */
/* Validação estrita de datas                                                           */
/* ------------------------------------------------------------------------------------ */

const DATE_ONLY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;
const INSTANT_REGEX = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?Z$/;

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function isRealCalendarDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1) return false;
  const daysInMonth = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= daysInMonth[month - 1];
}

/** Data civil 'YYYY-MM-DD' que existe no calendário (rejeita '2026-02-30' e '2026-1-5'). */
export function isDateOnly(value: unknown): value is DateOnly {
  if (typeof value !== 'string') return false;
  const match = DATE_ONLY_REGEX.exec(value);
  if (!match) return false;
  return isRealCalendarDate(Number(match[1]), Number(match[2]), Number(match[3]));
}

/** Instante 'YYYY-MM-DDTHH:MM:SS(.fração)?Z', sempre UTC com 'Z' (sem offset, sem espaço). */
export function isInstant(value: unknown): value is Instant {
  if (typeof value !== 'string') return false;
  const match = INSTANT_REGEX.exec(value);
  if (!match) return false;
  const [year, month, day, hour, minute, second] = match.slice(1).map(Number);
  return isRealCalendarDate(year, month, day) && hour <= 23 && minute <= 59 && second <= 59;
}

/* ------------------------------------------------------------------------------------ */
/* Query de domínio -> parâmetros de wire                                               */
/* ------------------------------------------------------------------------------------ */

export type MyStudiesWireParams = Record<string, string>;

export interface MyStudyCreatorsQuery {
  companyId?: number;
  search?: string;
}

/** Id aceito pelo backend: inteiro seguro e positivo (o resto vira 422). */
function isPositiveId(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}

/**
 * Forma canônica da consulta: status sem repetidos na ordem de `FLUX_STUDY_STATUS_CODES`, busca
 * aparada e página inteira >= 1. Serve de chave de cache; `toWireParams(q)` é igual a
 * `toWireParams(normalizeMyStudiesQuery(q))`. Não muta a entrada e é idempotente.
 */
export function normalizeMyStudiesQuery(query: MyStudiesQuery): MyStudiesQuery {
  const { pageNumber } = query;

  return {
    ...query,
    pageNumber: Number.isInteger(pageNumber) && pageNumber >= 1 ? pageNumber : 1,
    statuses: FLUX_STUDY_STATUS_CODES.filter((code) => query.statuses.includes(code)),
    search: query.search.trim(),
  };
}

/** Ids de estudo como o backend aceita: só positivos, sem repetidos e em ordem crescente. */
export function normalizeStudyIds(ids: readonly number[]): number[] {
  return Array.from(new Set(ids.filter(isPositiveId))).sort((a, b) => a - b);
}

/** Só as chaves que chegam ao wire: `companyId` positivo e `search` aparado e não vazio. */
export function normalizeCreatorsQuery(query?: MyStudyCreatorsQuery): MyStudyCreatorsQuery {
  const normalized: MyStudyCreatorsQuery = {};
  if (!query) return normalized;

  if (isPositiveId(query.companyId)) {
    normalized.companyId = query.companyId;
  }

  const search = typeof query.search === 'string' ? query.search.trim() : '';
  if (search) {
    normalized.search = search;
  }

  return normalized;
}

/**
 * Único lugar com os nomes de parâmetro de wire da lista. Só emite a chave que carrega valor;
 * `product_key` é acrescentado pelo slice de API.
 */
export function toWireParams(rawQuery: MyStudiesQuery): MyStudiesWireParams {
  const query = normalizeMyStudiesQuery(rawQuery);
  const params: MyStudiesWireParams = {
    page: String(query.pageNumber),
    per_page: String(query.pageSize),
    ref: query.dateReference === 'creation' ? 'created' : 'last_exec',
  };

  // Intervalo só vai inteiro: com uma ponta malformada some o par (invertido segue como veio).
  if (query.dateRange && isDateOnly(query.dateRange.start) && isDateOnly(query.dateRange.end)) {
    params.from = query.dateRange.start;
    params.to = query.dateRange.end;
  }

  if (query.company !== 'all' && isPositiveId(query.company.companyId)) {
    params.empresa = String(query.company.companyId);
  }

  if (query.createdBy === 'me') {
    params.criado_por = 'me';
  } else if (query.createdBy !== 'all' && isPositiveId(query.createdBy.userId)) {
    params.criado_por = String(query.createdBy.userId);
  }

  if (query.statuses.length > 0) {
    params.status = query.statuses.join(',');
  }

  if (query.search) {
    params.q = query.search;
  }

  return params;
}

export function toCreatorsWireParams(query?: MyStudyCreatorsQuery): MyStudiesWireParams {
  const { companyId, search } = normalizeCreatorsQuery(query);
  const params: MyStudiesWireParams = {};

  if (companyId !== undefined) {
    params.empresa = String(companyId);
  }

  if (search !== undefined) {
    params.q = search;
  }

  return params;
}

/**
 * `ids` em CSV (ver `normalizeStudyIds`). O limite de 50 é do chamador, e sem ids válidos o
 * resultado é `ids: ''` (o backend responde 422): o chamador deve usar `skip`.
 */
export function toStatusWireParams(ids: readonly number[]): MyStudiesWireParams {
  return { ids: normalizeStudyIds(ids).join(',') };
}

/* ------------------------------------------------------------------------------------ */
/* Helpers de leitura do wire                                                           */
/* ------------------------------------------------------------------------------------ */

type WireRecord = Record<string, unknown>;

function isRecord(value: unknown): value is WireRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asRecord(value: unknown, where: string): WireRecord {
  if (!isRecord(value)) {
    throw new MyStudiesContractError(`${where}: objeto esperado`);
  }
  return value;
}

function requireId(value: unknown, where: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new MyStudiesContractError(`${where}: id numérico esperado`);
  }
  return value;
}

function requireDateOnly(value: unknown, field: string, where: string): DateOnly {
  if (!isDateOnly(value)) {
    throw new MyStudiesContractError(`${where}: campo "${field}" ausente ou malformado (DateOnly esperado)`);
  }
  return value;
}

function nullableDateOnly(value: unknown): DateOnly | null {
  return isDateOnly(value) ? value : null;
}

function nullableInstant(value: unknown): Instant | null {
  return isInstant(value) ? value : null;
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
}

function asStatus(value: unknown): FluxStudyStatusCode | null {
  return typeof value === 'string' && isFluxStudyStatusCode(value) ? value : null;
}

/** Só os dois valores do CHECK do backend passam; qualquer outro (ou ausente) é desconhecido. */
function asOrigin(value: unknown): StudyOrigin | null {
  return value === 'api' || value === 'tela' ? value : null;
}

function toCompanyRef(raw: unknown, where: string): StudyCompanyRef {
  const ref = asRecord(raw, where) as Partial<RawStudyCompanyRef> & WireRecord;
  return { id: requireId(ref.id, where), name: asText(ref.nome) };
}

function toCreatorRef(raw: unknown, where: string): StudyCreatorRef | null {
  if (raw === null || raw === undefined) return null;
  const ref = asRecord(raw, where) as Partial<RawStudyCreatorRef> & WireRecord;
  return { id: requireId(ref.id, where), name: asText(ref.nome) };
}

/* ------------------------------------------------------------------------------------ */
/* Raw -> domínio                                                                       */
/* ------------------------------------------------------------------------------------ */

export function toMyStudy(raw: RawMyStudy): MyStudy {
  const study = asRecord(raw, 'estudo') as Partial<RawMyStudy> & WireRecord;
  const id = requireId(study.id, 'estudo');
  const where = `estudo ${id}`;
  const horizon = asRecord(study.horizonte, `${where}: campo "horizonte"`);

  const result: MyStudy = {
    id,
    name: asText(study.nome),
    company: toCompanyRef(study.empresa, `${where}: campo "empresa"`),
    creator: toCreatorRef(study.criador, `${where}: campo "criador"`),
    createdAt: nullableInstant(study.criado_em),
    sentAt: nullableInstant(study.enviado_em),
    origin: asOrigin(study.origem),
    horizon: {
      start: requireDateOnly(horizon.inicio, 'horizonte.inicio', where),
      end: horizon.fim === null ? null : requireDateOnly(horizon.fim, 'horizonte.fim', where),
    },
    status: asStatus(study.status),
    statusLabel: asText(study.status_rotulo),
    internalError: study.erro_interno === true,
    lastExecution: nullableInstant(study.ultima_execucao),
    lastResponse: nullableInstant(study.ultima_resposta),
    executionCount: asCount(study.n_execucoes),
    scenarioCount: asCount(study.n_cenarios),
    blockCount: asCount(study.n_blocos),
  };
  // Flux Upload: a chave só existe no wire do Upload; texto vazio conta como sem arquivo
  if ('arquivo' in study) {
    result.file = typeof study.arquivo === 'string' && study.arquivo.trim() !== '' ? study.arquivo : null;
  }
  return result;
}

/** Linha (ou cenário, bloco, opção) fora do contrato, deixada de fora do resultado. */
export interface MyStudiesRejectedRow {
  /** Id da linha quando é um número finito. */
  id: number | null;
  /** Mensagem do `MyStudiesContractError`. */
  reason: string;
}

/** Roda `convert` isolando só o `MyStudiesContractError`; qualquer outro erro propaga. */
function convertIsolated<T>(
  item: unknown,
  convert: (value: unknown) => T,
  rejected: MyStudiesRejectedRow[],
): T | undefined {
  try {
    return convert(item);
  } catch (error) {
    if (!(error instanceof MyStudiesContractError)) throw error;
    const id = isRecord(item) && typeof item.id === 'number' && Number.isFinite(item.id)
      ? item.id
      : null;
    rejected.push({ id, reason: error.message });
    return undefined;
  }
}

function convertEach<T>(
  items: unknown[],
  convert: (value: unknown) => T,
  rejected: MyStudiesRejectedRow[],
): T[] {
  const converted: T[] = [];
  items.forEach((item) => {
    const value = convertIsolated(item, convert, rejected);
    if (value !== undefined) converted.push(value);
  });
  return converted;
}

/** Envelope inutilizável é erro (não sucesso vazio): o consumidor precisa saber que falhou. */
function requireEnvelope(value: unknown, where: string): WireRecord {
  return asRecord(value, where);
}

function requireList(value: unknown, where: string): unknown[] {
  if (!Array.isArray(value)) {
    throw new MyStudiesContractError(`${where}: lista esperada`);
  }
  return value;
}

/**
 * Uma linha malformada nunca derruba a página: ela vai para `rejected` e as válidas seguem em
 * ordem. `total`, `pageNumber` e `pageSize` ficam como o backend mandou. `scopeHasStudies` só é
 * `false` com `escopo_tem_estudos` estritamente `false`: ausente, nulo ou inválido cai no lado
 * seguro (#2003), o vazio com os filtros, e não o "empresa sem estudos", que esconde a barra.
 * Envelope inutilizável (`data` que não é objeto, `items` que não é lista) lança
 * `MyStudiesContractError`.
 */
export function toMyStudiesPageResult(
  raw: RawMyStudiesPage,
): { page: MyStudiesPage; rejected: MyStudiesRejectedRow[] } {
  const rejected: MyStudiesRejectedRow[] = [];
  const page = requireEnvelope(raw, 'página de estudos') as Partial<RawMyStudiesPage> & WireRecord;
  const items = requireList(page.items, 'página de estudos: campo "items"');
  return {
    page: {
      items: convertEach(items, (item) => toMyStudy(item as RawMyStudy), rejected),
      total: asCount(page.total),
      pageNumber: asCount(page.page),
      pageSize: asCount(page.per_page),
      scopeHasStudies: page.escopo_tem_estudos !== false,
    },
    rejected,
  };
}

export function toMyStudiesPage(raw: RawMyStudiesPage): MyStudiesPage {
  return toMyStudiesPageResult(raw).page;
}

/** Página com a contagem de linhas descartadas, para a tela avisar que houve descarte. */
export interface MyStudiesListResult extends MyStudiesPage {
  rejectedCount: number;
}

export function toMyStudiesListResult(
  raw: RawMyStudiesPage,
): { list: MyStudiesListResult; rejected: MyStudiesRejectedRow[] } {
  const { page, rejected } = toMyStudiesPageResult(raw);
  return { list: { ...page, rejectedCount: rejected.length }, rejected };
}

function toMyStudyBlock(raw: unknown, scenarioId: number): MyStudyBlock {
  const block = asRecord(raw, `cenário ${scenarioId}: bloco`) as Partial<RawMyStudyBlock> & WireRecord;
  const id = requireId(block.id, `cenário ${scenarioId}: bloco`);
  const where = `bloco ${id}`;
  const { rodada, membro } = block;

  return {
    id,
    model: asText(block.modelo).toLowerCase(),
    forecastDate: requireDateOnly(block.data_previsao, 'data_previsao', where),
    member: typeof membro === 'string' && membro.trim() !== '' ? membro : null,
    // Q7: qualquer hora inteira de 0 a 23 (0 e 12 na prática; 6 e 18 em blocos antigos).
    runTime: typeof rodada === 'number' && Number.isInteger(rodada) && rodada >= 0 && rodada <= 23 ? rodada : null,
    biasRemoval: block.remocao_vies === true,
    start: requireDateOnly(block.inicio, 'inicio', where),
    end: requireDateOnly(block.fim, 'fim', where),
    cropStart: nullableDateOnly(block.recorte_inicio),
    cropEnd: nullableDateOnly(block.recorte_fim),
    cropRegion: typeof block.recorte_regiao === 'string' ? block.recorte_regiao : null,
  };
}

function toMyStudyScenario(raw: unknown, rejected: MyStudiesRejectedRow[]): MyStudyScenario {
  const scenario = asRecord(raw, 'cenário') as Partial<RawMyStudyScenario> & WireRecord;
  const id = requireId(scenario.id, 'cenário');

  return {
    id,
    name: asText(scenario.nome),
    blocks: convertEach(
      requireList(scenario.blocos, `cenário ${id}: campo "blocos"`),
      (block) => toMyStudyBlock(block, id),
      rejected,
    ),
  };
}

/** Bloco malformado sai do seu cenário; cenário sem id válido sai da lista. */
export function toMyStudyScenariosResult(
  raw: RawMyStudyScenario[],
): { scenarios: MyStudyScenario[]; rejected: MyStudiesRejectedRow[] } {
  const rejected: MyStudiesRejectedRow[] = [];
  const scenarios = convertEach(
    requireList(raw, 'cenários'),
    (item) => toMyStudyScenario(item, rejected),
    rejected,
  );
  return { scenarios, rejected };
}

export function toMyStudyScenarios(raw: RawMyStudyScenario[]): MyStudyScenario[] {
  return toMyStudyScenariosResult(raw).scenarios;
}

/**
 * Retrato do `/status/`. Diferente da lista, chave AUSENTE fica fora do retrato (lida como
 * `undefined`): a mescla do polling mantém o valor da linha, e um backend que mande só os campos da
 * nota 4 (`{id, status, ultima_resposta, n_execucoes}`) não apaga a última execução nem o erro
 * interno. Chave presente e malformada cai no fallback, como na lista.
 */
function toMyStudyStatusSnapshot(raw: unknown): MyStudyStatusSnapshot {
  const snapshot = asRecord(raw, 'status de estudo') as Partial<RawMyStudyStatus> & WireRecord;
  const result: MyStudyStatusSnapshot = { id: requireId(snapshot.id, 'status de estudo') };

  if (snapshot.status !== undefined) result.status = asStatus(snapshot.status);
  if (snapshot.status_rotulo !== undefined) result.statusLabel = asText(snapshot.status_rotulo);
  if (snapshot.ultima_execucao !== undefined) {
    result.lastExecution = nullableInstant(snapshot.ultima_execucao);
  }
  if (snapshot.ultima_resposta !== undefined) {
    result.lastResponse = nullableInstant(snapshot.ultima_resposta);
  }
  if (snapshot.n_execucoes !== undefined) result.executionCount = asCount(snapshot.n_execucoes);
  if (snapshot.erro_interno !== undefined) result.internalError = snapshot.erro_interno === true;
  return result;
}

export function toMyStudyStatusSnapshotsResult(
  raw: RawMyStudyStatus[],
): { snapshots: MyStudyStatusSnapshot[]; rejected: MyStudiesRejectedRow[] } {
  const rejected: MyStudiesRejectedRow[] = [];
  const snapshots = convertEach(requireList(raw, 'status de estudos'), toMyStudyStatusSnapshot, rejected);
  return { snapshots, rejected };
}

export function toMyStudyStatusSnapshots(raw: RawMyStudyStatus[]): MyStudyStatusSnapshot[] {
  return toMyStudyStatusSnapshotsResult(raw).snapshots;
}

function toStudyCompanyOption(raw: unknown): StudyCompanyOption {
  const company = asRecord(raw, 'empresa do filtro') as Partial<RawStudyCompany> & WireRecord;

  return {
    id: requireId(company.id, 'empresa do filtro'),
    name: asText(company.nome),
    studyCount: asCount(company.total_estudos),
  };
}

/**
 * Empresa sem id válido sai de `options`; os totais ficam como o backend mandou. Envelope
 * inutilizável (`data` que não é objeto, `itens` que não é lista) lança `MyStudiesContractError`.
 */
export function toStudyCompaniesSummaryResult(
  raw: RawStudyCompanies,
): { summary: StudyCompaniesSummary; rejected: MyStudiesRejectedRow[] } {
  const rejected: MyStudiesRejectedRow[] = [];
  const summary = requireEnvelope(raw, 'empresas do filtro') as
    Partial<RawStudyCompanies> & WireRecord;
  const itens = requireList(summary.itens, 'empresas do filtro: campo "itens"');

  return {
    summary: {
      totalStudies: asCount(summary.total_estudos),
      totalCompanies: asCount(summary.total_empresas),
      options: convertEach(itens, toStudyCompanyOption, rejected),
    },
    rejected,
  };
}

export function toStudyCompaniesSummary(raw: RawStudyCompanies): StudyCompaniesSummary {
  return toStudyCompaniesSummaryResult(raw).summary;
}

function toStudyCreatorOption(raw: unknown): StudyCreatorOption {
  const creator = asRecord(raw, 'criador do filtro') as Partial<RawStudyCreator> & WireRecord;
  const id = requireId(creator.id, 'criador do filtro');

  return {
    id,
    name: asText(creator.nome),
    company: toCompanyRef(creator.empresa, `criador ${id}: campo "empresa"`),
  };
}

export function toStudyCreatorOptionsResult(
  raw: RawStudyCreator[],
): { options: StudyCreatorOption[]; rejected: MyStudiesRejectedRow[] } {
  const rejected: MyStudiesRejectedRow[] = [];
  const options = convertEach(requireList(raw, 'criadores do filtro'), toStudyCreatorOption, rejected);
  return { options, rejected };
}

export function toStudyCreatorOptions(raw: RawStudyCreator[]): StudyCreatorOption[] {
  return toStudyCreatorOptionsResult(raw).options;
}
